import { PasswordProps } from "@/types/authentication";
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";
import { IoIosEye } from "react-icons/io";
import { IoIosEyeOff } from "react-icons/io";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import { TiTick } from "react-icons/ti";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { changePasswordschema } from "@/utils/schemaHelper";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";

import { ToastSuccessMessages } from "@/enums/errorMessages";
import { z } from "zod";
import { changePasswordApi } from "@/modules/authenticationService";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";

const PasswordChange: React.FC<PasswordProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return;
  const dispatch = useDispatch();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const loading = useSelector((state: RootState) => state.app.loading);

  const form = useForm<z.infer<typeof changePasswordschema>>({
    resolver: zodResolver(changePasswordschema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",

      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof changePasswordschema>) => {
    if (loading) return;
    dispatch(setLoading(true));

    try {
      await changePasswordApi(values);
      onClose();
      toastMessage("success", ToastSuccessMessages.PASSWORD_CHANGED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem className="max-h-18 ">
                  <FormLabel> Current Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.currentPassword
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showOldPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value &&
                        !form.formState.errors.currentPassword && (
                          <TiTick className="text-green-700" />
                        )}
                    </div>
                    <div
                      className="absolute right-7 top-2"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem className="max-h-18 ">
                  <FormLabel>New Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.newPassword
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.newPassword && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                    <div
                      className="absolute right-7 top-2"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="max-h-18 ">
                  <FormLabel>Confirm Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.confirmPassword
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value &&
                        !form.formState.errors.confirmPassword && (
                          <TiTick className="text-green-700" />
                        )}
                    </div>
                    <div
                      className="absolute right-7 top-2"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <div className="flex justify-center ">
              <ReusableButton
                label="Change"
                className="w-full"
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChange;
